import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Calendar, CreditCard, FileText, TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { PageHeader } from '@/layouts/AppLayout';
import { formatCurrency, formatDateOnly, formatInt } from '@/lib/utils';

// ── API types ─────────────────────────────────────────────────────────────────

interface DashboardMetrics {
  totalActiveBookings: number;
  revenueThisMonth: number;
  outstandingCommissions: { count: number; amount: number };
  checkInsNextWeek: number;
}

interface AttentionBooking {
  id: string;
  bookingCode: string;
  guestName: string;
  tentativeUntil?: string;
  createdAt?: string;
  hotel: { id: string; name: string; city: string };
  client: { id: string; name: string };
}

interface DashboardAttention {
  expiringTentatives: AttentionBooking[];
  pendingVouchers: AttentionBooking[];
  unsettledCommissions: {
    count: number;
    totalAmount: number;
    byHotel: { hotelId: string; hotelName: string; count: number; amount: number }[];
  };
}

interface BookingRow {
  id: string;
  bookingCode: string;
  guestName: string;
  status: string;
  checkIn: string;
  checkOut: string;
  amount: number;
  hotel: { id: string; name: string; city: string };
  client: { id: string; name: string };
}

interface BookingsPage {
  data: BookingRow[];
  meta: { total: number; page: number; limit: number };
}

// ── Metric card ───────────────────────────────────────────────────────────────

function MetricCard({
  title,
  value,
  sub,
  icon: Icon,
  accent,
}: {
  title: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  accent?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-neutral-500">{title}</p>
            <p
              className={`mt-1 truncate text-3xl font-bold tracking-tight ${
                accent ? 'text-tangerine-600' : 'text-neutral-900'
              }`}
            >
              {value}
            </p>
            {sub && <p className="mt-1 text-xs text-neutral-400">{sub}</p>}
          </div>
          <div
            className={`rounded-lg p-2.5 ${
              accent ? 'bg-tangerine-50 text-tangerine-600' : 'bg-neutral-100 text-neutral-500'
            }`}
            aria-hidden="true"
          >
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Recent bookings ───────────────────────────────────────────────────────────

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

function RecentBookings({ bookings }: { bookings: BookingRow[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Recent Bookings</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {bookings.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-neutral-400">No bookings yet.</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {bookings.map((b) => (
              <li key={b.id}>
                <Link
                  to={`/bookings/${b.id}`}
                  className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-neutral-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-medium text-neutral-500">
                        {b.bookingCode}
                      </span>
                      <Badge variant={b.status as any}>{statusLabel(b.status)}</Badge>
                    </div>
                    <p className="mt-0.5 truncate text-sm font-medium text-neutral-700">
                      {b.guestName}
                    </p>
                    <p className="text-xs text-neutral-400">
                      {b.hotel.name}, {b.hotel.city} &middot; {b.client.name}
                    </p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="font-semibold text-neutral-700">
                      {formatCurrency(b.amount)}
                    </p>
                    <p className="text-xs text-neutral-400">
                      {formatDateOnly(b.checkIn)} – {formatDateOnly(b.checkOut)}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-neutral-100 px-6 py-3">
          <Link
            to="/bookings"
            className="text-xs font-medium text-neutral-500 hover:text-neutral-700"
          >
            View all bookings &rarr;
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Attention panel ───────────────────────────────────────────────────────────

function AttentionPanel({ attention }: { attention: DashboardAttention }) {
  const hasAnything =
    attention.expiringTentatives.length > 0 ||
    attention.pendingVouchers.length > 0 ||
    attention.unsettledCommissions.count > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <AlertTriangle className="h-4 w-4 text-warning-700" aria-hidden="true" />
          Needs Attention
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pb-5">
        {!hasAnything && (
          <p className="text-sm text-neutral-400">Everything looks good.</p>
        )}

        {attention.expiringTentatives.length > 0 && (
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Expiring Tentatives
            </h3>
            <ul className="space-y-2">
              {attention.expiringTentatives.map((b) => (
                <li key={b.id}>
                  <Link
                    to={`/bookings/${b.id}`}
                    className="flex items-center justify-between rounded-md border border-neutral-200 bg-warning-50 px-3 py-2 hover:bg-neutral-100"
                  >
                    <div className="min-w-0">
                      <span className="font-mono text-xs font-medium text-neutral-500">
                        {b.bookingCode}
                      </span>
                      <p className="truncate text-sm text-neutral-700">{b.guestName}</p>
                      <p className="text-xs text-neutral-400">
                        {b.hotel.name} &middot; {b.client.name}
                      </p>
                    </div>
                    <div className="ml-3 shrink-0 text-right text-xs text-neutral-500">
                      Expires {b.tentativeUntil ? formatDateOnly(b.tentativeUntil) : '—'}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {attention.pendingVouchers.length > 0 && (
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Pending Vouchers
            </h3>
            <ul className="space-y-2">
              {attention.pendingVouchers.map((b) => (
                <li key={b.id}>
                  <Link
                    to={`/bookings/${b.id}`}
                    className="flex items-center justify-between rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 hover:bg-neutral-100"
                  >
                    <div className="min-w-0">
                      <span className="font-mono text-xs font-medium text-neutral-500">
                        {b.bookingCode}
                      </span>
                      <p className="truncate text-sm text-neutral-700">{b.guestName}</p>
                      <p className="text-xs text-neutral-400">
                        {b.hotel.name} &middot; {b.client.name}
                      </p>
                    </div>
                    <FileText className="ml-3 h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {attention.unsettledCommissions.count > 0 && (
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Unsettled Commissions
            </h3>
            <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium text-neutral-700">
                  {formatInt(attention.unsettledCommissions.count)} commission
                  {attention.unsettledCommissions.count !== 1 ? 's' : ''}
                </span>
                <span className="font-semibold text-neutral-700">
                  {formatCurrency(attention.unsettledCommissions.totalAmount)}
                </span>
              </div>
              <ul className="space-y-1">
                {attention.unsettledCommissions.byHotel.map((h) => (
                  <li
                    key={h.hotelId}
                    className="flex items-center justify-between text-xs text-neutral-500"
                  >
                    <span className="truncate">{h.hotelName}</span>
                    <span className="ml-3 shrink-0">
                      {formatInt(h.count)} &times; {formatCurrency(h.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { data: metrics, isPending: metricsPending } = useQuery({
    queryKey: ['dashboard', 'metrics'],
    queryFn: () => api.get<DashboardMetrics>('/dashboard/metrics'),
  });

  const { data: attention, isPending: attentionPending } = useQuery({
    queryKey: ['dashboard', 'attention'],
    queryFn: () => api.get<DashboardAttention>('/dashboard/attention'),
  });

  const { data: recentBookings } = useQuery({
    queryKey: ['bookings', 'recent'],
    queryFn: () =>
      api.get<BookingsPage>(
        '/bookings?page=1&limit=5&sortBy=createdAt&sortOrder=desc',
      ),
  });

  const isLoading = metricsPending || attentionPending;

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" />

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner className="h-6 w-6" />
        </div>
      )}

      {!isLoading && metrics && (
        <>
          {/* Metric cards — tangerine used only for Revenue MTD */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title="Active Bookings"
              value={formatInt(metrics.totalActiveBookings)}
              icon={Calendar}
            />
            <MetricCard
              title="Revenue MTD"
              value={formatCurrency(metrics.revenueThisMonth)}
              icon={TrendingUp}
              accent
            />
            <MetricCard
              title="Outstanding Commissions"
              value={formatInt(metrics.outstandingCommissions.count)}
              sub={formatCurrency(metrics.outstandingCommissions.amount)}
              icon={CreditCard}
            />
            <MetricCard
              title="Check-ins Next 7 Days"
              value={formatInt(metrics.checkInsNextWeek)}
              icon={Calendar}
            />
          </div>

          {/* Main content: recent bookings + attention panel */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <RecentBookings bookings={recentBookings?.data ?? []} />
            </div>
            <div>
              {attention && <AttentionPanel attention={attention} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
